import { useCallback, useEffect } from 'react';

import { HOME_IDLE_RELOAD_MS } from '@/bridge/app-management.bridge';
import { AppManagementLocalService } from '@/bridge/AppManagementLocalService';

/**
 * Hook that exposes app management via the bridge layer (no direct Capacitor from React).
 * Tries native reload when available; falls back to web reload on failure.
 */
export function useAppManagement() {
  const appReload = useCallback(() => {
    void AppManagementLocalService.appReload();
  }, []);

  return { appReload };
}

/** Minimum delay to avoid accidental immediate reload loop (e.g. if HOME_IDLE_RELOAD_MS is 0 or undefined). */
const MIN_IDLE_RELOAD_MS = 60 * 1000; // 1 minute

/**
 * Schedules an app reload after the given idle delay. Cleans up on unmount (e.g. when leaving the page).
 * @param delayMs Optional delay in ms before reload; defaults to 1 hour. Never less than 1 minute.
 */
export function useIdleAppReload(delayMs: number = HOME_IDLE_RELOAD_MS): void {
  useEffect(() => {
    const safeDelay =
      Number.isFinite(delayMs) && delayMs >= MIN_IDLE_RELOAD_MS ? delayMs : MIN_IDLE_RELOAD_MS;
    const timeoutId = window.setTimeout(() => {
      void AppManagementLocalService.appReload();
    }, safeDelay);
    return () => window.clearTimeout(timeoutId);
  }, [delayMs]);
}
